import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { NewsCategory, NewsItem, Product, ProductCategory } from '../types';
import productsData from '../data/products.json';
import categoriesData from '../data/categories.json';
import { api } from '../lib/apiClient';
import { getActiveNews, getActiveProducts } from '../lib/contentStatus';
import { setDynamicCategories } from '../lib/productCategories';

const VALID_NEWS_CATEGORIES: NewsCategory[] = ['Product', 'Logistics', 'Market Insight'];

// News is authored through the CMS and may contain a typo'd `category`
// value — guard against that silently making an article disappear from
// every News page cluster.
const normalizeNewsCategory = (item: NewsItem): NewsItem => ({
  ...item,
  category: VALID_NEWS_CATEGORIES.includes(item.category) ? item.category : 'Market Insight'
});

const products = productsData as unknown as Product[];
const categories = categoriesData as unknown as ProductCategory[];

// Category-derived UI (mega menu, filters) reads from this module-level
// singleton, so it only needs to be populated once at startup.
setDynamicCategories(categories);

interface DataContextType {
  products: Product[];
  news: NewsItem[];
  categories: ProductCategory[];
  activeProducts: Product[];
  activeNews: NewsItem[];
  // News is fetched from the CMS API; products/categories are still static.
  isLoading: boolean;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

const activeProducts = getActiveProducts(products);

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let active = true;

    api
      .getNews()
      .then(({ news: fetchedNews }) => {
        if (!active) return;
        setNews(fetchedNews.map(normalizeNewsCategory));
      })
      .catch(() => {
        if (!active) return;
        setNews([]);
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const activeNews = useMemo(() => getActiveNews(news), [news]);

  const value = useMemo<DataContextType>(
    () => ({
      products,
      news,
      categories,
      activeProducts,
      activeNews,
      isLoading
    }),
    [news, activeNews, isLoading]
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = () => {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used within DataProvider');
  return ctx;
};
